import * as React from 'react'

import Button from '@doordash/component-button'
import {
  IdentificationField,
  Select,
  TextField,
} from '@doordash/component-fields'
import Toggle from '@doordash/component-toggle'
import {
  Text,
  Breakpoints,
  Colors,
  InlineChildren,
  StackChildren,
} from '@doordash/design-language'
import MediaQuery from 'react-responsive'
import Form from '../../../../utilities/Form'
// tslint:disable-next-line:max-line-length
import { validators as sharedValidators } from '../../../../utilities/validators'
import {
  checkNeedsLicenseInfo,
  processVehicleTypes,
} from '../../../../utilities/vehicle'
import { BirthdateInputSplit } from '../../../atoms/birthdate-input'
import { InsuranceCheckBox } from '../../../atoms/insurance-checkbox'
import VehicleTypeDropdown from '../../../atoms/vehicle-type-dropdown'
import {
  DocumentAcceptanceModal,
  DocumentTypes,
} from '../../../molecules/document-acceptance-modal'

import { STATE_CODES } from '../../../../constants'

import t from '../strings'

import { BackgroundCheck as Tag } from '../../../../models/selectorAnchors'

type Props = {
  uniqueLink?: string
  requestedBgCheckCopy?: boolean
  error?: string
  onSubmit?: (any) => Promise<any>
  doSubmit?: (
    uniqueLink: string,
    backgroundInfo: object,
    requestedBgCheckCopy: boolean
  ) => Promise<any>
  vehicleTypes?: any
}

class USA extends React.Component<Props> {
  public static fields = {
    vehicleType: -1,
    licenseNumber: '',
    licenseState: '',
    insuranceCheck: '',
    ssn: '',
    birthday: '',
    requestedBgCheckCopy: false,
  }

  public static defaultProps = {
    requestedBgCheckCopy: false,
  }

  public static validators: any = {
    licenseNumber: sharedValidators.licenseNumber,
    licenseState: sharedValidators.licenseState,
    vehicleType: sharedValidators.vehicleType,
    insuranceCheck: sharedValidators.insuranceCheck,
    ssn: sharedValidators.ssn,
    birthday: sharedValidators.birthday,
    requestedBgCheckCopy: sharedValidators.requestedBgCheckCopy,
  }

  public handleSubmit = async data => {
    const { doSubmit, uniqueLink, requestedBgCheckCopy } = this.props
    try {
      const res = await doSubmit(uniqueLink, data, requestedBgCheckCopy)
      return res
    } catch (e) {
      throw e
    }
  }

  public handleVehicleChange = (name, handleChange) => selectedID => {
    handleChange(name, selectedID)
    // This actually is only here to trigger a re-render to update
    // the validators
    this.setState({})
    const needsLicenseInfo = this.props.vehicleTypes[selectedID]
      .needs_license_info
    this.populateVehicleValidators(needsLicenseInfo)
  }

  // This is needed for when the user chooses a vehicle that
  // does not require a driver's license
  // We don't want to force validation on those fields on
  // things like bicycles
  public populateVehicleValidators = (needsLicenseInfo: boolean) => {
    if (needsLicenseInfo) {
      USA.validators = {
        ...USA.validators,
        licenseNumber: sharedValidators.licenseNumber,
        licenseState: sharedValidators.licenseState,
        insuranceCheck: sharedValidators.insuranceCheck,
      }
    } else {
      USA.validators = {
        ...USA.validators,
        licenseNumber: sharedValidators.hidden,
        licenseState: sharedValidators.hidden,
        insuranceCheck: sharedValidators.hidden,
      }
    }
  }

  public render() {
    const { vehicleTypes } = this.props

    return (
      <React.Fragment>
        <Text color={Colors.SystemRed60} styles={Text.Styles.MajorPageTitle}>
          {t.title()}
        </Text>

        <Form
          onSubmit={this.handleSubmit}
          validators={USA.validators}
          fields={USA.fields}
        >
          {(fields, handleChange, handleSubmit, { isLoading }) => (
            <form onSubmit={handleSubmit}>
              <StackChildren size={StackChildren.Sizes.Large}>
                <div data-anchor-id={Tag.VehicleSelect}>
                  <VehicleTypeDropdown
                    accessibilityDescribedBy={'vehicleType'}
                    label={t.vehicleTypeLabel()}
                    description={t.required()}
                    vehicleTypes={processVehicleTypes(vehicleTypes)}
                    onChange={this.handleVehicleChange(
                      'vehicleType',
                      handleChange
                    )}
                    {...fields.vehicleType}
                  />
                </div>

                {checkNeedsLicenseInfo(fields, vehicleTypes) && (
                  <React.Fragment>
                    <MediaQuery minWidth={Breakpoints.Desktop + 1}>
                      <InlineChildren>
                        <div
                          data-anchor-id={Tag.LicenseState}
                          style={{ width: '100%' }}
                        >
                          <Select
                            accessibilityDescribedBy={'licenseState'}
                            label={t.licenseStateLabel()}
                            description={t.required()}
                            disabledValue={t.licenseStateDisabled()}
                            onChange={handleChange.bind(this, 'licenseState')}
                            options={STATE_CODES}
                            {...fields.licenseState}
                          />
                        </div>
                        <TextField
                          accessibilityDescribedBy={'licenseNumber'}
                          label={t.licenseNumberLabel()}
                          description={t.required()}
                          onChange={handleChange.bind(this, 'licenseNumber')}
                          autoComplete="off"
                          data-anchor-id={Tag.LicenseNumber}
                          {...fields.licenseNumber}
                        />
                      </InlineChildren>
                    </MediaQuery>
                    <MediaQuery maxWidth={Breakpoints.Desktop}>
                      <div data-anchor-id={Tag.LicenseState}>
                        <Select
                          accessibilityDescribedBy={'licenseState'}
                          label={t.licenseStateLabel()}
                          description={t.required()}
                          disabledValue={t.licenseStateDisabled()}
                          onChange={handleChange.bind(this, 'licenseState')}
                          options={STATE_CODES}
                          data-anchor-id={Tag.LicenseState}
                          {...fields.licenseState}
                        />
                      </div>
                      <TextField
                        accessibilityDescribedBy={'licenseNumber'}
                        label={t.licenseNumberLabel()}
                        description={t.required()}
                        onChange={handleChange.bind(this, 'licenseNumber')}
                        autoComplete="off"
                        data-anchor-id={Tag.LicenseNumber}
                        {...fields.licenseNumber}
                      />
                    </MediaQuery>

                    <InsuranceCheckBox
                      id="inset-insurance"
                      accessibilityDescribedBy={'insuranceCheckbox'}
                      label={t.insuranceCertificationLabel()}
                      onChange={handleChange.bind(this, 'insuranceCheck')}
                      anchorId={Tag.InsuranceCheckbox}
                      {...fields.insuranceCheck}
                    />
                  </React.Fragment>
                )}
                <BirthdateInputSplit
                  id="birthdate-input-split"
                  label={t.birthdate()}
                  onChange={handleChange.bind(this, 'birthday')}
                  birthdayField={fields.birthday}
                  data-anchor-id={Tag.BirthdateContainer}
                  monthAnchorId={Tag.BirthdateMonth}
                  dayAnchorId={Tag.BirthdateDay}
                  yearAnchorId={Tag.BirthdateYear}
                  {...fields.birthday}
                />
                <IdentificationField
                  country="US"
                  accessibilityDescribedBy={'socialSecurityNumber'}
                  onChange={handleChange.bind(this, 'ssn')}
                  label={t.socialSecurityNumber()}
                  description={t.required()}
                  autoComplete="off"
                  data-anchor-id={Tag.PersonalIdentificationNumber}
                  {...fields.ssn}
                />
                <div>
                  <DocumentAcceptanceModal
                    type={DocumentTypes.BackgroundCheckDisclosure}
                    anchorId={Tag.BackgroundCheckDisclosure}
                  />
                  <DocumentAcceptanceModal
                    type={DocumentTypes.AdditionalDisclosuresDocument}
                    anchorId={Tag.AdditionalDisclosuresDocument}
                  />
                  <DocumentAcceptanceModal
                    type={DocumentTypes.AuthAndReleaseDocument}
                    anchorId={Tag.AuthAndReleaseDocument}
                  />
                </div>
                <div data-anchor-id={Tag.BackgroundCheckCopyToggle}>
                  <Toggle
                    accessibilityDescribedBy={'receivedBackgroundCheckCopy'}
                    label={t.receivesCopy()}
                    alignToggle={Toggle.Alignment.Center}
                    type={Toggle.Types.Checkbox}
                    onChange={handleChange.bind(this, 'requestedBgCheckCopy')}
                    isSelected={fields.requestedBgCheckCopy.value}
                  />
                </div>

                <Text
                  styles={Text.Styles.PageSubtext}
                  lineHeight={Text.LineHeights.Large}
                >
                  {t.terms()}
                </Text>

                <Button
                  accessibilityDescribedBy={'submitBackgroundCheck'}
                  onClick={async () => {
                    try {
                      await handleSubmit()
                    } catch (e) {
                      console.warn(e)
                    }
                  }}
                  state={
                    isLoading ? Button.States.Loading : Button.States.Default
                  }
                  data-anchor-id={Tag.PrimaryAction}
                >
                  {t.continue()}
                </Button>
              </StackChildren>
            </form>
          )}
        </Form>
      </React.Fragment>
    )
  }
}

export default USA
